# Monitoring and troubleshooting

SEEK is responsible for the availability of the SEEK API,
however there are important aspects to monitor with your integration to ensure it is working as expected.

## Keeping up to date

### SEEK API status

The [SEEK API status page] provides real-time information on the availability of the SEEK API, such as outages or degraded performance.
Planned maintenance is also communicated in advance to minimise disruption to your integration.

We recommend you subscribe to the status page to receive notifications of any issues that may impact your integration.

### Release notes

We regularly update the SEEK API with new features, improvements, and bug fixes.
To stay informed of these changes, subscribe to our [release notes].
This ensures that you are aware of any upcoming changes that may impact your integration and can plan accordingly.

### Developer Dashboard

The [Developer Dashboard] provides a range of tools to help you monitor your integration.
You should subscribe to alerts to be notified of issues with your webhooks, such as delivery failures.

The dashboard offers an [Integration Health] tool, which provides a summary of traffic and errors across your integration.
This can help to identify and [troubleshoot](#troubleshooting-steps) any issues with your integration.

There are a number of further tools available in the dashboard for debugging and administration tasks.
These include:

- Viewing your webhook endpoint performance and delivery status, and resending any failed events
- Viewing your configured hirer relationships
- Managing your credentials
- Viewing job posting features such as branding and locations

We highly recommend you familiarise yourself with the Developer Dashboard and use it to monitor your integration.

## Troubleshooting steps

1. **Check the [SEEK API status page]**: Ensure there are no ongoing issues with the SEEK API that may be impacting your integration.
1. **Check your logs**: While SEEK provides a number of tools to assist, ultimately your own software will also need to be monitored.
   We recommend you set up logging and alerting for [error responses from the SEEK API].
   Ensure that you log the details of any errors that occur in your integration, and related [tracing information].
1. **Review the panel troubleshooting guide**: If your problem relates to our embedded panels, review our documentation on [panel troubleshooting].
   This includes common problems and steps to troubleshoot issues with panels.
1. **Use the Developer Dashboard**: The [Integration Health] tool can provide a summary of traffic and errors across your integration.
   This can help to identify any issues with your integration.

   For example, if you have identified an error response from the SEEK API in your logs,
   you can use the Integration Health tool to review the frequency of this error, in the context of all traffic to your integration.

   Otherwise, if you haven’t identified the cause of an issue in your logs, it can be used to find patterns and errors.

1. **Review the error responses documentation**: If you have identified your error, look up the code in our [error responses from the SEEK API] documentation.
   This will provide more information on the error and how to resolve it.
1. **Review the documentation**: Many pages in the documentation include troubleshooting steps.
   Review the relevant documentation for your use case to see if there are any specific troubleshooting steps.
1. **Ask for help**: If you are unable to resolve the issue independently, [reach out to SEEK’s support team][SEEK’s support team].
   Be sure to pass along [tracing information] from your logs when asking for assistance.

   If you have identified an error response from the SEEK API in the Integration Health tool,
   you can use the sharing tools on the page or specific request details to provide to SEEK support.

## Ask for help

If you encounter any issues with your integration, [reach out to SEEK’s support team][SEEK’s support team].
Be sure to pass along [tracing information] from your logs when asking for assistance.

If you embed panels in your software,
review our documentation on [panel troubleshooting] and include logs in your support request.

[Developer Dashboard]: /manage
[error responses from the SEEK API]: /graphql/error-responses
[integration health]: /manage/integration-health
[panel troubleshooting]: /introduction/panels#troubleshooting
[release notes]: /introduction/release-notes
[seek api status page]: https://status.seek.com
[seek’s support team]: https://au.employer.seek.com/contactus
[tracing information]: /graphql/in-practice#tracing-requests
